import React, { Component } from "react"
import { Form, Button, Overlay } from "react-bootstrap"
import { FaCaretDown, FaSearch } from "react-icons/fa"
import { List, ListRowProps } from "react-virtualized"
import Checkbox from "./Checkbox"

export interface Item {
  label: string
  value?: string
  checked?: boolean
}

export interface ListItem {
  label: string
  value: string
  checked: boolean
}

interface CheckboxListProps {
  title: string
  items: Item[]
  selectedValues: string[]
  onClose?: (items: string[]) => void
  className: string
  disabled: boolean
}

interface CheckboxListState {
  open: boolean
  searchText: string
  items: ListItem[]
  filteredItems?: ListItem[]
  filtered?: boolean
}

export default class CheckboxList extends Component<CheckboxListProps, CheckboxListState> {
  state: CheckboxListState = { 
    open: false,
    items: this.props.items.map(i => { return {label: i.label, value: i.value || i.label, checked: !!i.checked } }),
    searchText: "",
    filteredItems: []
  }
  nameInput: HTMLInputElement | undefined
  toggleElement = React.createRef<HTMLDivElement>()
  listElement = React.createRef<List>()

  componentDidMount() {
    this.resetFilterItems(this.props.selectedValues)
  }

  componentDidUpdate(prevProps: CheckboxListProps) {
    const arraysEqual = (a1: Item[], a2: Item[]): boolean => {
      if (a1.length !== a2.length) {
        return false
      }
      const a1Sorted = [...a1].filter(item => !!item).map(item => (item.value || item.label).toLowerCase()).sort()
      const a2Sorted = [...a2].filter(item => !!item).map(item => (item.value || item.label).toLowerCase()).sort()
      return a1Sorted.every((value, index) => value === a2Sorted[index])
    }
    if (this.props.disabled !== prevProps.disabled) {
      this.resetFilterItems(this.props.selectedValues)
    }
    if (this.props.title !== prevProps.title) {
      this.resetFilterItems(this.props.selectedValues)
    } else {
      if (!!this.props.selectedValues) {
        let selectedItems = this.props.items.filter(i => this.props.selectedValues.includes(i.value || i.label)) || []
        let prevSelectedItems = prevProps.items.filter(i => this.props.selectedValues.includes(i.value || i.label)) || []
        if (!arraysEqual(selectedItems, prevSelectedItems) || !arraysEqual(this.props.items || [], prevProps.items || [])) {
          this.resetFilterItems(this.props.selectedValues)
        }
      } else {
        if (!arraysEqual(this.props.items, prevProps.items)) {
          this.resetFilterItems(this.props.selectedValues)
        }
      }
    }
  }

  resetFilterItems(checkedValues: string[]) {
    const checkAll = checkedValues.length === this.props.items.length
    
    let items = this.props.items.map(i => { return {label: i.label, value: i.value || i.label, checked: checkedValues.includes(i.value || i.label) } })
    items.unshift({label: "(Select All)", value: "#ALL#", checked: checkAll})
    
    this.setState({ items: items })
  }

  toggleDialog = () => {
    this.setState({ open: !this.props.disabled && !this.state.open })
  }
  setData = () => {
    this.props.onClose && this.props.onClose(this.state.items.filter(i => i.checked).map(i => i.value || i.label))
    this.toggleDialog()
  }
  changeTextFilter = (value: string) => this.setState({ searchText: value })
  checkboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const checked = e.target.checked
    const index = this.state.items.findIndex(item => String(item.value) === value)
    if (index === 0) {
      this.setState(prevState => {
        let newItems = [...prevState.items]
        newItems.map(item => item.checked = checked)
        return { items: newItems }
      })
    } else {
      this.setState(prevState => {
        const newItems = [...prevState.items]
        newItems[index].checked = checked
        newItems[0].checked = newItems.slice(1).every((item) => item.checked)
        return { items: newItems }
      })
    }
    this.listElement.current && this.listElement.current.forceUpdateGrid()
  }

  getFilteredItems = () => {
    const { items, searchText } = this.state
    if (searchText.length > 0) {
      return items.filter(item => item.label.toString().toLowerCase().includes(searchText.toLowerCase()))
    }
    return items
  }

  rowRenderer = (props: ListRowProps) => {
    const item = this.getFilteredItems()[props.index]
    return (
      <Checkbox className="pl-2" id={item.value} label={item.label} value={item.value || item.label} key={item.value} checked={item.checked} onChange={this.checkboxChange} style={props.style} />
    )
  }

  render() {
    return (
      <div className={`${this.props.className} ${this.props.disabled ? "disabled" : ""}`}>
        <div onClick={this.toggleDialog} className="toggle" ref={this.toggleElement}>
          {this.props.title}
          <FaCaretDown  />
        </div>
        <Overlay container={this.toggleElement} target={this.toggleElement.current} show={this.state.open} placement="bottom-start" rootClose onHide={this.toggleDialog} popperConfig={{modifiers:[{name:"offset", options:{offset:[-13, 12]}}]}}>
          <div id="list" className="filter-section">
            <div className="search-box">
              <Form.Control autoFocus tabIndex={0} type="text" size="sm" placeholder="Search" value={this.state.searchText} onChange={(e) => this.changeTextFilter(e.target.value)} ref={(input: HTMLInputElement) => input && input.focus()} />
              <FaSearch />
              <div className="search-box-items">
                <List ref={this.listElement} width={300} height={100} rowCount={this.getFilteredItems().length} rowHeight={20} rowRenderer={this.rowRenderer} />
              </div>
              <div className="pt-1">
                  <Button size="sm" variant="secondary" className="float-right ml-1" onClick={this.toggleDialog}>CANCEL</Button>
                  <Button size="sm" variant="secondary" className="float-right" disabled={this.state.items.every((item) => !item.checked)} onClick={this.setData}>OK</Button>
                </div>
            </div>
          </div>
        </Overlay>
      </div>
    )
  }

  static defaultProps = { selectedValues: [], className: "checkbox-list", disabled: false }
}
